import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, AlertCircle, Mic, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

const MicTest3 = () => {
  const navigate = useNavigate();
  const [micStatus, setMicStatus] = useState<'checking' | 'detected' | 'not-detected'>('checking');
  const [isPlayingTest, setIsPlayingTest] = useState(false);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);

  // Detect browser for specific instructions
  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) {
      return {
        icon: '🔴',
        steps: [
          'Look for the microphone icon 🔴 in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else if (userAgent.includes('Firefox')) {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon 🎤 in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else if (userAgent.includes('Safari')) {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon 🎤 in your browser\'s address bar',
          'Click on it and select "Allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    }
  };

  const browserInstructions = getBrowserInstructions();

  // Check microphone access on component mount
  useEffect(() => {
    checkMicrophoneAccess();
  }, []);

  const checkMicrophoneAccess = async () => {
    try {
      setMicStatus('checking');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      // If we get here, microphone access was granted
      setMicStatus('detected');
      
      // Stop the stream immediately as we just needed to check access
      stream.getTracks().forEach(track => track.stop());
      
    } catch (error) {
      console.error('Microphone access denied:', error);
      setMicStatus('not-detected');
    }
  };

  const playTestAudio = async () => {
    if (isPlayingTest) return;
    
    setIsPlayingTest(true);
    
    try {
      // Create a test audio element
      const audio = new Audio();
      audio.src = 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task3-intro.mp3';
      audio.onended = () => setIsPlayingTest(false);
      audio.onerror = () => setIsPlayingTest(false);
      
      testAudioRef.current = audio;
      await audio.play();
    } catch (error) {
      console.error('Error playing test audio:', error);
      setIsPlayingTest(false);
    }
  };

  const handleStartTask3 = () => {
    navigate('/task-three');
  };

  const getStatusIcon = () => {
    switch (micStatus) {
      case 'checking':
        return <AlertCircle className="h-8 w-8 text-yellow-500 animate-pulse" />;
      case 'detected':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'not-detected':
        return <AlertCircle className="h-8 w-8 text-red-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (micStatus) {
      case 'checking':
        return 'Checking microphone access...';
      case 'detected':
        return 'Microphone access granted! You\'re ready for Task 3.';
      case 'not-detected':
        return 'Microphone access denied. Please allow microphone access to continue.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-8">
          <div className="text-center space-y-6">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-gray-900">Task 3 Microphone Test</h1>
              <p className="text-gray-600">Let's make sure your microphone is working properly for Task 3</p>
            </div>

            <div className="flex flex-col items-center space-y-4">
              {getStatusIcon()}
              <p className="text-lg font-medium text-gray-700">{getStatusMessage()}</p>
            </div>

            {micStatus === 'not-detected' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-yellow-800 mb-2">
                  How to enable microphone access:
                </h3>
                <div className="space-y-2">
                  <p className="text-yellow-700">
                    <span className="font-medium">{browserInstructions.icon}</span> {browserInstructions.steps[0]}
                  </p>
                  <p className="text-yellow-700">
                    {browserInstructions.steps[1]}
                  </p>
                  <p className="text-yellow-700">
                    {browserInstructions.steps[2]}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={checkMicrophoneAccess}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Mic className="h-4 w-4" />
                Try Again
              </Button>

              {micStatus === 'detected' && (
                <>
                  <Button
                    onClick={playTestAudio}
                    disabled={isPlayingTest}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Play className="h-4 w-4" />
                    {isPlayingTest ? 'Playing...' : 'Test Audio'}
                  </Button>

                  <Button
                    onClick={handleStartTask3}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Start Task 3
                  </Button>
                </>
              )}
            </div>

            <div className="text-sm text-gray-500">
              <p>Task 3 is a conversation-based speaking test where you'll discuss a topic with an AI.</p>
              <p>Make sure your microphone is working and you're in a quiet environment.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MicTest3;